import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type MarketPerformanceSubscription from 'lumen/models/market-performance/subscription';

export const METRIC_TYPES = [
  'bi_forecast',
  'budget',
  'finance_forecast',
  'ly',
  'net_sdly',
  'otb',
  'rms_forecast',
  'sdly',
  'user_forecast',
  // Pickup rows: the change since `pickup_days` ago (see the serializer).
  'otb:pickup:1',
  'otb:pickup:7',
  'bi_forecast:pickup:1',
  'bi_forecast:pickup:7',
  // Transient / group columns of the segments view (see `segmentMetricType`).
  'transient',
  'transient:sdly',
  'transient:net_sdly',
  'transient:ly',
  'group',
  'group:sdly',
  'group:net_sdly',
  'group:ly',
] as const;

export type MetricType = (typeof METRIC_TYPES)[number];

export const KPIS = ['occupancy', 'rooms', 'adr', 'revenue', 'revpar'] as const;

export type Kpi = (typeof KPIS)[number];

// What a table sub-column can show: the KPIs plus the group-only cells.
export type TableKpi = Kpi | 'otb_blk' | 'rev_blk';

export interface MetricVariance {
  absolute: number;
  percent: number;
}

export type VarianceSet = Record<Kpi, MetricVariance>;

export interface MarketPerformanceMetrics {
  adr: number | null;
  occupancy: number | null;
  revenue: number;
  revpar: number | null;
  rooms: number;

  variance: VarianceSet | null;
}

// The segment metrics are only sent with `segments=true`.
export interface MarketPerformanceSegments {
  group_blk?: MarketPerformanceMetrics | null;
  group_otb?: MarketPerformanceMetrics | null;
  transient?: MarketPerformanceMetrics | null;
}

export type MarketPerformanceTotals = Record<
  MetricType,
  { totals: MarketPerformanceMetrics | null } & MarketPerformanceSegments
>;

export interface MarketPerformanceMeta {
  count: number;
  group_by: string;
  page_count: number;
  totals: MarketPerformanceTotals;
  warnings: string[];
}

export default class MarketPerformanceDataItem extends Model {
  [Type] = 'market-performance/data-item' as const;

  @belongsTo('market-performance/subscription', {
    async: false,
    inverse: 'dataItems',
  })
  declare subscription: MarketPerformanceSubscription | null;

  @attr('string') declare date: string | null;
  @attr() declare groupBlk: MarketPerformanceMetrics | null;
  @attr() declare groupOtb: MarketPerformanceMetrics | null;
  @attr('string') declare metricType: MetricType;

  @attr() declare totals: MarketPerformanceMetrics | null;

  // Segment breakdowns, only present with `segments=true`.
  @attr() declare transient: MarketPerformanceMetrics | null;
}
